import React, { useEffect, useMemo, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/core/styles';

import { creerDeploiement } from '../deploiements/deploiementApi';
import SitewebResume from './SitewebResume';

const etapes = ['Contexte', 'Avant deploiement', 'Pendant deploiement', 'Apres deploiement', 'Decision', 'Resume'];
const CLE_BROUILLON = 'deploiement_siteweb_brouillon_v1';

const useStyles = makeStyles(() => ({
  bulleInfo: { fontSize: '0.95rem', lineHeight: 1.45, maxWidth: 460, padding: '10px 12px' },
}));

const etatInitial = {
  environnementCible: '',
  operateur: '',
  branche: '',
  commit: '',
  version: '',
  validationsPreDeploiement: {
    envTransfertPresent: false,
    secretsNonVersionnes: false,
    dryRunExecute: false,
    dryRunValide: false,
    modeControlledParDefaut: false,
    tlsExplicite: false,
    sslVerifyActif: false,
    deployDeleteActif: false,
    justificationDelete: '',
  },
  executionDeploiement: {
    commandeExecution: 'bash scriptTransfert.sh --dry-run',
    modeExecution: 'controlled',
    fullScopeConfirme: false,
    suppressionRevue: false,
    incidents: '',
  },
  validationsPostDeploiement: {
    pagesClesOk: false,
    apiCritiquesOk: false,
    logsOk: false,
    fichiersPresentsOk: false,
    notes: '',
  },
  decisionFinale: '',
  commentaireDecision: '',
};

const validerContexte = (etat) => Boolean(
  etat.environnementCible &&
  etat.operateur.trim() &&
  (etat.branche.trim() || etat.commit.trim() || etat.version.trim())
);

const validerPre = (etat) => {
  const pre = etat.validationsPreDeploiement;
  if (!(pre.envTransfertPresent && pre.secretsNonVersionnes && pre.dryRunExecute && pre.dryRunValide && pre.modeControlledParDefaut && pre.tlsExplicite && pre.sslVerifyActif)) {
    return false;
  }
  if (pre.deployDeleteActif && !pre.justificationDelete.trim()) {
    return false;
  }
  return true;
};

const validerExecution = (etat) => {
  const exe = etat.executionDeploiement;
  const pre = etat.validationsPreDeploiement;
  if (!exe.commandeExecution.trim() || !exe.modeExecution) return false;
  if (exe.modeExecution === 'full' && !exe.fullScopeConfirme) return false;
  if (pre.deployDeleteActif && !exe.suppressionRevue) return false;
  return true;
};

const validerPost = (etat) => {
  const p = etat.validationsPostDeploiement;
  return Boolean(p.pagesClesOk && p.apiCritiquesOk && p.logsOk && p.fichiersPresentsOk);
};

const validerDecision = (etat) => Boolean(etat.decisionFinale);

const etapeValide = (etat, index) => {
  if (index === 0) return validerContexte(etat);
  if (index === 1) return validerPre(etat);
  if (index === 2) return validerExecution(etat);
  if (index === 3) return validerPost(etat);
  if (index === 4) return validerDecision(etat);
  if (index === 5) return validerContexte(etat) && validerPre(etat) && validerExecution(etat) && validerPost(etat) && validerDecision(etat);
  return false;
};

const construireRapport = (etat) => ({
  type: 'deploiement_siteweb',
  dateRapport: new Date().toISOString(),
  environnementCible: etat.environnementCible,
  operateur: etat.operateur,
  referenceVersion: { branche: etat.branche, commit: etat.commit, version: etat.version },
  validationsPreDeploiement: { ...etat.validationsPreDeploiement },
  executionDeploiement: { ...etat.executionDeploiement },
  validationsPostDeploiement: { ...etat.validationsPostDeploiement },
  decisionFinale: etat.decisionFinale,
  commentaireDecision: etat.commentaireDecision,
  rapport: {
    titre: 'Rapport de deploiement siteweb',
    sections: [
      { nom: 'Contexte', contenu: { environnementCible: etat.environnementCible, operateur: etat.operateur, branche: etat.branche, commit: etat.commit, version: etat.version } },
      { nom: 'Avant deploiement', contenu: { ...etat.validationsPreDeploiement } },
      { nom: 'Pendant deploiement', contenu: { ...etat.executionDeploiement } },
      { nom: 'Apres deploiement', contenu: { ...etat.validationsPostDeploiement } },
      { nom: 'Decision finale', contenu: { decisionFinale: etat.decisionFinale, commentaireDecision: etat.commentaireDecision } },
    ],
  },
});

function SitewebForm({ onCreation }) {
  const classes = useStyles();
  const [etat, setEtat] = useState(etatInitial);
  const [etapeActive, setEtapeActive] = useState(0);
  const [soumissionEnCours, setSoumissionEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [pretSauvegarde, setPretSauvegarde] = useState(false);
  const [derniereSauvegarde, setDerniereSauvegarde] = useState('');

  const rapport = useMemo(() => construireRapport(etat), [etat]);

  const changerChamp = (n, v) => setEtat((p) => ({ ...p, [n]: v }));
  const changerPre = (n, v) => setEtat((p) => ({ ...p, validationsPreDeploiement: { ...p.validationsPreDeploiement, [n]: v } }));
  const changerExe = (n, v) => setEtat((p) => ({ ...p, executionDeploiement: { ...p.executionDeploiement, [n]: v } }));
  const changerPost = (n, v) => setEtat((p) => ({ ...p, validationsPostDeploiement: { ...p.validationsPostDeploiement, [n]: v } }));

  useEffect(() => {
    try {
      const brut = localStorage.getItem(CLE_BROUILLON);
      if (brut) {
        const s = JSON.parse(brut);
        if (s && s.etat) {
          setEtat({ ...etatInitial, ...s.etat });
          setEtapeActive(typeof s.etapeActive === 'number' ? s.etapeActive : 0);
          setDerniereSauvegarde(s.derniereSauvegarde || '');
          setSucces('Un brouillon siteweb a ete restaure automatiquement.');
        }
      }
    } catch (e) {
      setErreur('Impossible de restaurer le brouillon siteweb.');
    } finally {
      setPretSauvegarde(true);
    }
  }, []);

  useEffect(() => {
    if (!pretSauvegarde) return;
    try {
      const initial = JSON.stringify(etat) === JSON.stringify(etatInitial) && etapeActive === 0;
      if (initial) {
        localStorage.removeItem(CLE_BROUILLON);
        setDerniereSauvegarde('');
        return;
      }
      const t = new Date().toISOString();
      localStorage.setItem(CLE_BROUILLON, JSON.stringify({ etat, etapeActive, derniereSauvegarde: t }));
      setDerniereSauvegarde(t);
    } catch (e) {
      setErreur('Impossible de sauvegarder le brouillon siteweb.');
    }
  }, [etat, etapeActive, pretSauvegarde]);

  const caseInfo = (label, valeur, onChange, consigne) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <FormControlLabel control={<Checkbox color="primary" checked={valeur} onChange={(e) => onChange(e.target.checked)} />} label={label} />
      <Tooltip title={consigne} placement="right" arrow classes={{ tooltip: classes.bulleInfo }}>
        <IconButton size="medium" aria-label={'Informations - ' + label}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const suivant = () => {
    if (!etapeValide(etat, etapeActive)) {
      setErreur('Des informations critiques sont manquantes sur cette etape.');
      return;
    }
    setEtapeActive((p) => Math.min(p + 1, etapes.length - 1));
  };

  const precedent = () => setEtapeActive((p) => Math.max(p - 1, 0));

  const annuler = () => {
    const ok = window.confirm('Voulez-vous annuler ce deploiement siteweb en cours et supprimer le brouillon local ?');
    if (!ok) return;
    setEtat(etatInitial);
    setEtapeActive(0);
    setDerniereSauvegarde('');
    localStorage.removeItem(CLE_BROUILLON);
    setSucces('Le deploiement siteweb en cours a ete annule.');
  };

  const soumettre = async () => {
    if (!etapeValide(etat, 5)) {
      setErreur('Le formulaire est incomplet. Merci de finaliser toutes les validations.');
      return;
    }
    setSoumissionEnCours(true);
    setErreur('');
    try {
      const resultat = await creerDeploiement(rapport);
      setSucces('Rapport siteweb enregistre avec succes.');
      setEtat(etatInitial);
      setEtapeActive(0);
      setDerniereSauvegarde('');
      localStorage.removeItem(CLE_BROUILLON);
      if (onCreation) onCreation(resultat);
    } catch (err) {
      setErreur(err.message || 'Impossible d enregistrer le rapport siteweb.');
    } finally {
      setSoumissionEnCours(false);
    }
  };

  const vueContexte = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="siteweb-env-label">Environnement cible</InputLabel>
          <Select labelId="siteweb-env-label" label="Environnement cible" value={etat.environnementCible} onChange={(e) => changerChamp('environnementCible', e.target.value)}>
            <MenuItem value="dev">dev</MenuItem>
            <MenuItem value="prod">prod</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}><TextField fullWidth variant="outlined" label="Operateur" value={etat.operateur} onChange={(e) => changerChamp('operateur', e.target.value)} /></Grid>
      <Grid item xs={12} md={4}><TextField fullWidth variant="outlined" label="Version" value={etat.version} onChange={(e) => changerChamp('version', e.target.value)} /></Grid>
      <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Branche" value={etat.branche} onChange={(e) => changerChamp('branche', e.target.value)} /></Grid>
      <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Commit" value={etat.commit} onChange={(e) => changerChamp('commit', e.target.value)} helperText="Au moins un champ parmi branche/commit/version est obligatoire" /></Grid>
    </Grid>
  );

  const vuePre = (
    <Grid container spacing={1}>
      <Grid item xs={12}>{caseInfo('`.env.transfert` present et valide', etat.validationsPreDeploiement.envTransfertPresent, (v) => changerPre('envTransfertPresent', v), 'Confirmez la presence locale du fichier .env.transfert avec des valeurs a jour.')}</Grid>
      <Grid item xs={12}>{caseInfo('Secrets non versionnes', etat.validationsPreDeploiement.secretsNonVersionnes, (v) => changerPre('secretsNonVersionnes', v), 'Confirmez que .env.transfert et les secrets ne sont pas commits dans Git.')}</Grid>
      <Grid item xs={12}>{caseInfo('Dry-run execute', etat.validationsPreDeploiement.dryRunExecute, (v) => changerPre('dryRunExecute', v), 'Commande obligatoire avant tout transfert: bash scriptTransfert.sh --dry-run')}</Grid>
      <Grid item xs={12}>{caseInfo('Dry-run valide', etat.validationsPreDeploiement.dryRunValide, (v) => changerPre('dryRunValide', v), 'Validez le perimetre de fichiers et l absence de suppression inattendue.')}</Grid>
      <Grid item xs={12}>{caseInfo('Mode controlled confirme', etat.validationsPreDeploiement.modeControlledParDefaut, (v) => changerPre('modeControlledParDefaut', v), 'Par defaut DEPLOY_MODE=controlled. Utilisez full seulement avec justification explicite.')}</Grid>
      <Grid item xs={12}>{caseInfo('TLS explicite confirme', etat.validationsPreDeploiement.tlsExplicite, (v) => changerPre('tlsExplicite', v), 'Mode recommande DEPLOY_TLS_MODE=explicit.')}</Grid>
      <Grid item xs={12}>{caseInfo('Verification SSL active', etat.validationsPreDeploiement.sslVerifyActif, (v) => changerPre('sslVerifyActif', v), 'DEPLOY_SSL_VERIFY doit rester actif en nominal. Desactivation uniquement en diagnostic temporaire.')}</Grid>
      <Grid item xs={12}>{caseInfo('DEPLOY_DELETE active', etat.validationsPreDeploiement.deployDeleteActif, (v) => changerPre('deployDeleteActif', v), 'Si active, des fichiers distants peuvent etre supprimes. Confirmez uniquement si necessaire et maitrise.')}</Grid>
      {etat.validationsPreDeploiement.deployDeleteActif && (
        <Grid item xs={12}><TextField fullWidth variant="outlined" label="Justification de DEPLOY_DELETE" value={etat.validationsPreDeploiement.justificationDelete} onChange={(e) => changerPre('justificationDelete', e.target.value)} /></Grid>
      )}
      <Grid item xs={12}>
        <Paper style={{ padding: 12, background: '#f8fbff' }}>
          <Typography variant="subtitle2">Runbook siteweb (avant execution)</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>1) Verifiez .env.transfert et les parametres critiques.</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>2) Lancez `bash scriptTransfert.sh --dry-run`.</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>3) Passez en GO seulement si le dry-run est propre et compris.</Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const vueExe = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField fullWidth variant="outlined" label="Commande executee" value={etat.executionDeploiement.commandeExecution} onChange={(e) => changerExe('commandeExecution', e.target.value)} helperText="Exemple: bash scriptTransfert.sh" />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="siteweb-mode-label">Mode execution</InputLabel>
          <Select labelId="siteweb-mode-label" label="Mode execution" value={etat.executionDeploiement.modeExecution} onChange={(e) => changerExe('modeExecution', e.target.value)}>
            <MenuItem value="controlled">controlled</MenuItem>
            <MenuItem value="full">full</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {etat.executionDeploiement.modeExecution === 'full' && (
        <Grid item xs={12} md={6}>
          {caseInfo('Portee complete confirmee', etat.executionDeploiement.fullScopeConfirme, (v) => changerExe('fullScopeConfirme', v), 'Mode full = miroir complet. A reserver aux resynchronisations volontaires.')}
        </Grid>
      )}
      {etat.validationsPreDeploiement.deployDeleteActif && (
        <Grid item xs={12}>
          {caseInfo('Suppressions distantes revues', etat.executionDeploiement.suppressionRevue, (v) => changerExe('suppressionRevue', v), 'Confirmez que la liste de suppressions attendues a ete revue avant execution.')}
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={3} variant="outlined" label="Incidents / observations pendant execution" value={etat.executionDeploiement.incidents} onChange={(e) => changerExe('incidents', e.target.value)} />
      </Grid>
    </Grid>
  );

  const vuePost = (
    <Grid container spacing={1}>
      <Grid item xs={12}>{caseInfo('Pages cles valides', etat.validationsPostDeploiement.pagesClesOk, (v) => changerPost('pagesClesOk', v), 'Controlez accueil, login et parcours metier principal.')}</Grid>
      <Grid item xs={12}>{caseInfo('API critiques valides', etat.validationsPostDeploiement.apiCritiquesOk, (v) => changerPost('apiCritiquesOk', v), 'Controlez endpoints sante et API metier prioritaires.')}</Grid>
      <Grid item xs={12}>{caseInfo('Logs sans erreur critique', etat.validationsPostDeploiement.logsOk, (v) => changerPost('logsOk', v), 'Verifiez 5xx, erreurs ressources manquantes, erreurs TLS/proxy.')}</Grid>
      <Grid item xs={12}>{caseInfo('Fichiers attendus presents', etat.validationsPostDeploiement.fichiersPresentsOk, (v) => changerPost('fichiersPresentsOk', v), 'Confirmez qu aucun fichier utile n a ete supprime a tort.')}</Grid>
      <Grid item xs={12}><TextField fullWidth multiline rows={3} variant="outlined" label="Notes post-deploiement" value={etat.validationsPostDeploiement.notes} onChange={(e) => changerPost('notes', e.target.value)} /></Grid>
    </Grid>
  );

  const vueDecision = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="siteweb-decision-label">Decision finale</InputLabel>
          <Select labelId="siteweb-decision-label" label="Decision finale" value={etat.decisionFinale} onChange={(e) => changerChamp('decisionFinale', e.target.value)}>
            <MenuItem value="go">GO</MenuItem>
            <MenuItem value="go_avec_surveillance">GO avec surveillance</MenuItem>
            <MenuItem value="no_go">NO GO</MenuItem>
            <MenuItem value="rollback">ROLLBACK</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={8}><TextField fullWidth variant="outlined" label="Commentaire de decision" value={etat.commentaireDecision} onChange={(e) => changerChamp('commentaireDecision', e.target.value)} /></Grid>
    </Grid>
  );

  let contenu = vueContexte;
  if (etapeActive === 1) contenu = vuePre;
  if (etapeActive === 2) contenu = vueExe;
  if (etapeActive === 3) contenu = vuePost;
  if (etapeActive === 4) contenu = vueDecision;
  if (etapeActive === 5) contenu = <SitewebResume rapport={rapport} />;

  return (
    <Paper style={{ padding: 16 }}>
      <Typography variant="h5">Deploiement siteweb</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        Procedure guidee pour `scriptTransfert.sh` avec controles de securite et de perimetre.
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: 'block' }}>
        {derniereSauvegarde ? ('Brouillon sauvegarde automatiquement: ' + new Date(derniereSauvegarde).toLocaleString()) : 'Brouillon local non initialise.'}
      </Typography>

      <Stepper activeStep={etapeActive} alternativeLabel style={{ marginTop: 16, marginBottom: 16 }}>
        {etapes.map((e) => <Step key={e}><StepLabel>{e}</StepLabel></Step>)}
      </Stepper>

      {contenu}

      <Grid container spacing={2} style={{ marginTop: 12 }}>
        <Grid item><Button variant="outlined" disabled={etapeActive === 0 || soumissionEnCours} onClick={precedent}>Precedent</Button></Grid>
        <Grid item>
          {etapeActive < etapes.length - 1
            ? <Button variant="contained" color="primary" disabled={soumissionEnCours} onClick={suivant}>Suivant</Button>
            : <Button variant="contained" color="primary" disabled={soumissionEnCours} onClick={soumettre}>{soumissionEnCours ? 'Enregistrement...' : 'Soumettre le rapport'}</Button>}
        </Grid>
        <Grid item><Button variant="text" color="secondary" disabled={soumissionEnCours} onClick={annuler}>Annuler le deploiement en cours</Button></Grid>
      </Grid>

      <Snackbar open={Boolean(erreur)} autoHideDuration={5000} onClose={() => setErreur('')} message={erreur} />
      <Snackbar open={Boolean(succes)} autoHideDuration={4000} onClose={() => setSucces('')} message={succes} />
    </Paper>
  );
}

export default SitewebForm;




// WEBPACK FOOTER //
// src/components/deploiementsSiteweb/SitewebForm.js